import {AuthChangeEvent, FunctionsHttpError, Session} from '@supabase/supabase-js';
import {useRedirector} from '@tnc/shared';
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {AccountTypeT, ActingAsT, PasswordWithEmailT, VALID_ACCOUNT_TYPES} from '@/lib/types/authentication.types';
import {speculatorT, speculatorUpdateT} from '@/lib/types/speculator.types';
import {UserAccountsT} from '@/lib/types/user.types';

import {getAuthCallbackURL, getBaseURL} from '@/lib/helpers/urlHelpers';
import {SupabaseInstance} from '@/lib/utils';

/* ******************************************************************
 * AuthContext
 ****************************************************************** */
type HandleRegisterArgsT<T> = {
  data: T;
  accountType: AccountTypeT;
  silentSignIn?: boolean;
};

type AuthContextT = {
  // Properties
  authenticating: boolean;
  emailVerificationSent: boolean;
  isAuthenticated: boolean;
  isSpeculator: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isDistributor: boolean;
  isRetailer: boolean;
  isAdministrator: boolean;
  userAccounts: UserAccountsT;
  actingAs: ActingAsT;
  accountBalance: number;
  heldBalance: number;

  // Functions
  handleRegister: <T>({data, accountType, silentSignIn}: HandleRegisterArgsT<T>) => Promise<void>;
  handleSignOut: () => Promise<void>;
  handleUpdatePassword: (password: string, captchaToken: string) => Promise<void>;
  setActingAs: (actingAs: ActingAsT) => void;
  handleEmailAndPasswordSignIn: (credentials: PasswordWithEmailT) => Promise<void>;
  handleEmailAndPasswordSignUp: (credentials: PasswordWithEmailT) => Promise<void>;
  handleResendEmailVerification: (email?: string, captchaToken?: string) => Promise<void>;
  handleGoogleAuthentication: (accountType?: AccountTypeT) => Promise<void>;
  handleFacebookAuthentication: (accountType?: AccountTypeT) => Promise<void>;
  getUser: () => Promise<speculatorT>;
  getUserAccounts: () => Promise<UserAccountsT>;
  getUserAccountBalance: () => Promise<number>;
  refreshUserAccounts: () => Promise<void>;
  handleUpdateSpeculator: (speculator: speculatorUpdateT) => Promise<speculatorT>;
  validateAccountType: (accountType: AccountTypeT | undefined) => boolean;
};

const AuthContext = createContext<AuthContextT>({} as AuthContextT);

/* ******************************************************************
 * AuthProvider
 ****************************************************************** */
// Create a type for the complete auth state
type AuthStateT = {
  authenticating: boolean;
  emailVerificationSent: boolean;
  isAuthenticated: boolean;
  userAccounts: UserAccountsT;
  actingAs: ActingAsT;
};

export const AuthProvider = ({children}: {children: React.ReactNode}) => {
  // Constants
  const defaultUserAccounts: UserAccountsT = useMemo(
    () => ({
      speculator: null,
      verification: null,
      distributors: [],
      retailers: [],
      brands: [],
    }),
    []
  );

  // Combine related state into a single state object
  const [authState, setAuthState] = useState<AuthStateT>({
    authenticating: true,
    emailVerificationSent: false,
    isAuthenticated: false,
    userAccounts: defaultUserAccounts,
    actingAs: getActingAs(),
  });

  const [accountBalance, setAccountBalance] = useState<number>(0);
  const [heldBalance, setHeldBalance] = useState<number>(0);

  // Refs
  const isInitializedRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  const {clearReferralData, trackReferralSignup} = useRedirector();

  /**********************************************************************
   * USER FUNCTIONS
   **********************************************************************/
  const getUserAccounts = useCallback(async () => {
    try {
      const id = await SupabaseInstance.getCurrentUserId();
      if (!id) return defaultUserAccounts;

      const {data: userAccounts, error} = await SupabaseInstance.supabase.rpc('get_user_account_data').single();
      if (error || !userAccounts) {
        console.error('error in get user accounts', error);
        return defaultUserAccounts;
      }

      return userAccounts as UserAccountsT;
    } catch (error) {
      console.error('error in get user accounts', error);
      return defaultUserAccounts;
    }
  }, [defaultUserAccounts]);

  // Update auth state in a single operation
  const updateAuthState = useCallback((updates: Partial<AuthStateT>) => {
    setAuthState(prev => ({...prev, ...updates}));
  }, []);

  // Reset auth state in a single operation
  const resetAuth = useCallback(() => {
    setAuthState({
      authenticating: false,
      emailVerificationSent: false,
      isAuthenticated: false,
      userAccounts: defaultUserAccounts,
      actingAs: 'speculator',
    });
  }, [defaultUserAccounts]);

  // Sign in user with a single state update
  const signInUser = useCallback(async () => {
    // First check to see if the user has verified their email
    // Call supabase and check if the user has verified their email
    try {
      const user = await SupabaseInstance.getCurrentUser();

      if (!user.email_confirmed_at) {
        setAuthState({
          authenticating: false,
          emailVerificationSent: true,
          isAuthenticated: false,
          userAccounts: defaultUserAccounts,
          actingAs: 'speculator',
        });
        return;
      }
    } catch (error) {
      console.error('error in get current user', error);
      throw new Error('Failed to get current user');
    }

    // Fully SignIn User
    try {
      const userAccounts = await getUserAccounts();

      if (userAccounts.speculator && userAccounts.verification) {
        // Determine acting as based on user type and path
        const _actingAs = userAccounts.superAdmin ? 'superAdmin' : userAccounts.admin ? 'admin' : 'speculator';

        const newActingAs =
          _actingAs !== 'speculator' && window.location.pathname.includes('/admin')
            ? 'admin'
            : _actingAs === 'admin' && !window.location.pathname.includes('/admin')
              ? 'speculator'
              : 'speculator';

        // Update all auth state in a single operation
        updateAuthState({
          authenticating: false,
          isAuthenticated: true,
          userAccounts,
          actingAs: newActingAs,
        });
      } else {
        resetAuth();
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      resetAuth();
    }
  }, [getUserAccounts, resetAuth, updateAuthState]);

  const getUser = async () => {
    const id = await SupabaseInstance.getCurrentUserId();
    if (!id) throw new Error('User not found');

    const {data: userRecord, error} = await SupabaseInstance.supabase
      .from('Speculator')
      .select<string, speculatorT>()
      .eq('uid', id)
      .single<speculatorT | null>();

    if (error) throw new Error('Error getting user');
    if (!userRecord) throw new Error('User not found');
    return userRecord;
  };

  const handleUpdateSpeculator = async (speculator: speculatorUpdateT) => {
    let updatedSpeculator: speculatorT | null = null;
    try {
      const id = await SupabaseInstance.getCurrentUserId();
      if (!id) throw new Error('User not found');

      const {data, error} = await SupabaseInstance.supabase
        .from('Speculator')
        .update<speculatorUpdateT>(speculator)
        .eq('uid', id)
        .select<string, speculatorT>()
        .single();

      if (error) throw new Error('Error updating user');
      if (!data) throw new Error('User not found');

      updatedSpeculator = data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Error updating user');
    }

    try {
      await refreshUserAccounts();
    } catch (error) {
      console.error('Error refreshing user accounts:', error);
      throw new Error('Error refreshing user accounts');
    }

    if (!updatedSpeculator) throw new Error('User not found');
    return updatedSpeculator;
  };

  /**********************************************************************
   * GENERAL AUTH FUNCTIONS
   **********************************************************************/
  /*
   * Gets the users account balance
   */
  const getUserAccountBalance = useCallback(async () => {
    if (!authState.userAccounts.speculator) return 0;

    const id = await SupabaseInstance.getCurrentUserId();

    const {data: accountBalance, error} = await SupabaseInstance.supabase
      .from('user_account_balances')
      .select('*')
      .eq('user_uuid', id)
      .single();

    if (error && error.details !== 'The result contains 0 rows') throw new Error('Error getting user account balance');

    setAccountBalance(accountBalance?.available_balance || 0);
    setHeldBalance(accountBalance?.held_balance || 0);
    return accountBalance?.available_balance || 0;
  }, [authState.userAccounts.speculator]);

  useEffect(() => {
    getUserAccountBalance();
  }, [authState.userAccounts.speculator, getUserAccountBalance]);

  /**********************************************************************
   * GENERAL AUTH FUNCTIONS
   **********************************************************************/
  /*
   * Sets the actingAs value in local storage and state
   */
  const setActingAs = useCallback(
    (actingAs: ActingAsT) => {
      localStorage.setItem('actingAs', actingAs);
      updateAuthState({actingAs});
    },
    [updateAuthState]
  );

  /*
   * Handle the sign out of the current user
   */
  const handleSignOut = useCallback(async () => {
    await SupabaseInstance.supabase.auth.signOut();
    resetAuth();
  }, [resetAuth]);

  /**
   * Tracks a user signup referral if referral data exists
   */
  const trackSignupReferral = useCallback(async () => {
    await trackReferralSignup();

    clearReferralData();
  }, [clearReferralData, trackReferralSignup]);

  /*
   * Update the user speculator record
   */
  const handleRegister = useCallback(
    async <T,>({data, accountType, silentSignIn = false}: HandleRegisterArgsT<T>) => {
      try {
        // Call the registration edge function
        const response = await SupabaseInstance.supabase.functions.invoke('register', {
          body: {
            ...data,
            accountType,
          },
        });
        if (response.error) {
          // Handle FunctionsHttpError according to Supabase docs
          if (response.error instanceof FunctionsHttpError) {
            const errorData = await response.error.context.json();

            // Check for validation errors
            if (errorData?.error?.message === 'Validation failed' && errorData?.error?.errors) {
              const errors = errorData.error.errors as Record<string, string[]>;
              // Format the error for display
              const validationErrors = Object.entries(errors)
                .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                .join('\n');

              throw new Error('Please fix the following validation errors:', {
                cause: {
                  validationErrors: errors,
                  message: validationErrors,
                },
              });
            }

            // If we have some other error with a message, use that
            if (errorData?.error?.message) {
              throw new Error(errorData.error.message);
            }

            // If we couldn't parse the error or it's missing expected structure
            throw new Error('Unable to complete registration. Please try again.');
          }

          // If it's not a FunctionsHttpError
          throw new Error('Unable to complete registration. Please try again.');
        }

        await trackSignupReferral();

        if (silentSignIn) {
          try {
            await signInUser();
          } catch (signInError) {
            console.error('DEBUG: Sign-in after registration produced expected auth refresh', signInError);
            throw signInError;
          }
        }
      } catch (error) {
        console.error('ERROR SUBMITTING REGISTRATION FORM', error);
        throw error;
      }
    },
    [signInUser, trackSignupReferral]
  );

  /**
   * @private
   * A function to get the actingAs value from local storage. If the value is not
   * valid, it will default to 'speculator' and set the local storage value to 'speculator'
   *
   * @Note Use a function here to allow hoisting, since this will not be wrapped
   * in useCallback, do not expose to the context
   *
   * @returns ActingAsT
   */
  function getActingAs() {
    const _actingAs = localStorage.getItem('actingAs');

    // Runtime typeguard to ensure the value is a valid actingAs value
    if (_actingAs !== 'speculator' && _actingAs !== 'admin' && _actingAs !== 'superAdmin') {
      localStorage.setItem('actingAs', 'speculator');
      return 'speculator';
    }

    return _actingAs;
  }

  /**********************************************************************
   * Auth Event Handler
   **********************************************************************/
  /*
   * Handles the auth change event
   */
  const handleAuthChangeEvent = useCallback(
    async (event: AuthChangeEvent, session: Session | null) => {
      switch (event) {
        case 'INITIAL_SESSION':
          if (session) signInUser();
          break;
        case 'USER_UPDATED':
          if (session) signInUser();
          break;
        case 'TOKEN_REFRESHED':
          if (session) signInUser();
          break;
        case 'SIGNED_IN':
          if (!session) resetAuth();
          else signInUser();
          break;
        case 'SIGNED_OUT':
          resetAuth();
          break;
        case 'PASSWORD_RECOVERY': {
          resetAuth();
          break;
        }
        default:
          // Catch any unknown events and reset the auth state
          resetAuth();
          break;
      }
    },
    [resetAuth, signInUser]
  );

  /**********************************************************************
   * EMAIL AND PASSWORD AUTHENTICATION
   **********************************************************************/
  /*
   * Handle the sign in with email and password
   */
  const handleEmailAndPasswordSignIn = async (credentials: PasswordWithEmailT) => {
    try {
      const {error} = await SupabaseInstance.supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
        options: {
          captchaToken: credentials.captchaToken,
        },
      });
      if (error) throw new Error((error as Error).message);
      await signInUser();
    } catch (error) {
      console.error('error in handle email and password sign in', error);
      resetAuth();
      throw new Error((error as Error).message);
    }
  };

  /*
   * Handle the sign up with email and password
   */
  const handleEmailAndPasswordSignUp = async ({email, password, captchaToken}: PasswordWithEmailT) => {
    try {
      // check if user exists with that email
      const {data: existingEmail, error: emailError} = await SupabaseInstance.supabase
        .from('Speculator')
        .select('speculator_email')
        .eq('speculator_email', email.toLowerCase());

      if (emailError) {
        console.error('Error checking existing email:', emailError);
        throw new Error(emailError.message);
      }

      if (existingEmail && existingEmail.length > 0) {
        throw new Error('Email already exists');
      }

      // Sign up user with email and password
      const {data: userData, error} = await SupabaseInstance.supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken,
        },
      });

      if (error || !userData) {
        throw new Error('Authenticated user sign up failed');
      }

      // At this point the verification email has been sent
      // Set email verification sent state to notify the user
      updateAuthState({emailVerificationSent: true});
    } catch (error) {
      console.error('error in handle email and password sign up', error);
      resetAuth();
      throw new Error((error as Error).message);
    }
  };

  /*
   * Handle the resend email verification
   */
  const handleResendEmailVerification = async (email?: string, captchaToken?: string) => {
    const resetEmail = email || (await SupabaseInstance.getCurrentUser()).email;
    if (!resetEmail) throw new Error('User email not found');

    try {
      const {error} = await SupabaseInstance.supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${getBaseURL()}/speculator/settings/update-password`,
        captchaToken,
      });

      if (error) {
        console.error('error in handle resend email verification', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('error in handle resend email verification', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Handle the update password
   */
  const handleUpdatePassword = async (password: string, captchaToken: string) => {
    try {
      const {error} = await SupabaseInstance.supabase.functions.invoke('update-password', {
        body: {
          password,
          captchaToken,
        },
      });
      if (error) {
        console.error('error in handle update password', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('error in handle update password', error);
      throw new Error((error as Error).message);
    }
  };

  /**********************************************************************
   * SOCIAL AUTHENTICATION
   **********************************************************************/
  /*
   * Handles Google sign in
   */
  const handleGoogleAuthentication = async (accountType: AccountTypeT | undefined = 'speculator') => {
    const _accountType = validateAccountType(accountType) ? accountType : 'speculator';

    try {
      const {data, error} = await SupabaseInstance.supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${getAuthCallbackURL()}?account_type=${_accountType}`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('Error during OAuth sign-in', error);
        throw new Error((error as Error).message);
      } else {
        // Redirect the user to the provider's URL
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('error in handle google authentication', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Handles Facebook sign in
   */
  const handleFacebookAuthentication = async (accountType: AccountTypeT = 'speculator') => {
    const _accountType = validateAccountType(accountType) ? accountType : 'speculator';

    try {
      const {data, error} = await SupabaseInstance.supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${getAuthCallbackURL()}?account_type=${_accountType}`,
        },
      });

      if (error) {
        console.error('Error during OAuth sign-in', error);
        throw new Error((error as Error).message);
      } else {
        // Redirect the user to the provider's URL
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('error in handle facebook authentication', error);
      throw new Error((error as Error).message);
    }
  };

  /* ******************************************************************
   * Helper Functions
   ******************************************************************* */
  // Typeguard to ensure from is a valid AccountTypeT if passed in, default to speculator if not
  const validateAccountType = (accountType: AccountTypeT | undefined) => {
    return accountType && VALID_ACCOUNT_TYPES.includes(accountType) ? true : false;
  };

  /**
   * Refreshes the user's accounts and updates the state
   */
  const refreshUserAccounts = useCallback(async () => {
    const userAccounts = await getUserAccounts();
    setAuthState(prev => ({...prev, userAccounts}));
  }, [getUserAccounts]);

  /* ******************************************************************
   * Initialization
   ******************************************************************* */
  useEffect(() => {
    let authListener: {data: {subscription: {unsubscribe: () => void}}} | null = null;

    const Initialize = async () => {
      if (!mountedRef.current || isInitializedRef.current) return;

      try {
        // Process URL hash for OAuth redirects
        // This is needed for social login callbacks with access tokens
        if (window.location.hash && window.location.hash.includes('access_token')) {
          await SupabaseInstance.supabase.auth.getSession();
        }

        // Get the initial session first
        const {
          data: {session},
          error: sessionError,
        } = await SupabaseInstance.supabase.auth.getSession();
        if (sessionError) throw sessionError;

        // Set up the auth state change listener
        authListener = SupabaseInstance.supabase.auth.onAuthStateChange(async (event, session) => {
          try {
            await handleAuthChangeEvent(event, session);
          } catch (error) {
            console.error('error in auth listener', error);
          }
        });

        // Handle initial session
        if (session) {
          await signInUser();
        } else {
          resetAuth();
        }

        // Mark as initialized immediately to prevent double initialization
        isInitializedRef.current = true;
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mountedRef.current) resetAuth();
      }
    };

    Initialize();

    return () => {
      mountedRef.current = false;
      if (authListener?.data?.subscription) {
        authListener.data.subscription.unsubscribe();
      }
    };
  }, [authState.isAuthenticated, handleAuthChangeEvent, resetAuth, signInUser]);

  // Compute derived state
  const derivedState = useMemo(
    () => ({
      isSpeculator: !authState.authenticating && authState.isAuthenticated && !!authState.userAccounts.speculator,
      isAdmin: !authState.authenticating && authState.isAuthenticated && !!authState.userAccounts.admin,
      isSuperAdmin: !authState.authenticating && authState.isAuthenticated && !!authState.userAccounts.superAdmin,
      isAdministrator: !!authState.userAccounts.admin || !!authState.userAccounts.superAdmin,
      isDistributor: authState.userAccounts.distributors && authState.userAccounts.distributors.length > 0,
      isRetailer: authState.userAccounts.retailers && authState.userAccounts.retailers.length > 0,
    }),
    [authState]
  );

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        ...derivedState,
        setActingAs,
        handleRegister,
        handleSignOut,
        handleUpdatePassword,
        handleEmailAndPasswordSignIn,
        handleEmailAndPasswordSignUp,
        handleResendEmailVerification,
        handleGoogleAuthentication,
        handleFacebookAuthentication,
        accountBalance,
        heldBalance,
        getUser,
        getUserAccounts,
        getUserAccountBalance,
        validateAccountType,
        refreshUserAccounts,
        handleUpdateSpeculator,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/* ******************************************************************
 * useAuth Hook
 ****************************************************************** */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
